import { useEffect, useState } from 'react';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';

/**
 * Ambient connection cue.
 *
 * Instead of a hard error screen when the network drops, Leafnode shows a
 * quiet "signal lost" strip in the app's own language — dark slate, blurred,
 * non-blocking — plus a brief "signal restored" confirmation on reconnect.
 */
export const ConnectionStatus = () => {
  const { online, weak, justReconnected } = useNetworkStatus();
  const [visible, setVisible] = useState(false);

  const state = !online ? 'offline' : justReconnected ? 'restored' : weak ? 'weak' : null;

  useEffect(() => {
    if (state) {
      const id = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(id);
    }
    setVisible(false);
  }, [state]);

  if (!state) return null;

  const copy = {
    offline: {
      label: 'Signal lost',
      detail: 'You’re offline — browsing what’s already been received.',
      dot: 'bg-amber-400',
      ring: 'border-amber-400/30',
    },
    weak: {
      label: 'Weak signal',
      detail: 'Connection is slow — some transmissions may take a moment.',
      dot: 'bg-sky-400',
      ring: 'border-sky-400/30',
    },
    restored: {
      label: 'Signal restored',
      detail: 'You’re back online.',
      dot: 'bg-emerald-400',
      ring: 'border-emerald-400/30',
    },
  }[state];

  return (
    <div
      role="status"
      aria-live="polite"
      className={`fixed inset-x-0 bottom-0 z-[9998] flex justify-center px-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pointer-events-none transition-all duration-500 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
      }`}
    >
      <div
        className={`flex items-center gap-2.5 rounded-full border ${copy.ring} bg-slate-900/70 backdrop-blur-xl px-4 py-2 shadow-lg`}
      >
        <span className="relative flex h-2 w-2 flex-shrink-0">
          <span className={`absolute inline-flex h-full w-full rounded-full ${copy.dot} opacity-60 animate-ping`} />
          <span className={`relative inline-flex h-2 w-2 rounded-full ${copy.dot}`} />
        </span>
        <span className="text-[13px] font-medium tracking-wide text-slate-200">{copy.label}</span>
        <span className="hidden sm:inline text-[13px] text-slate-400">{copy.detail}</span>
      </div>
    </div>
  );
};

export default ConnectionStatus;
